import { useDictionaryContext } from "@/components/dictionaries/DictionaryContext";
import DictionaryForm from "@/components/dictionaries/DictionaryForm";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import PageContent from "@/components/PageContent";
import { Input } from "@/components/ui/input";
import { isRequired } from "@/lib/form";
import { usePayrollDepartmentsDictionary } from "@/modules/payrollDepartments/hooks/usePayrollDepartmentsDictionary";
import { useTranslation } from "react-i18next";
import SaveButton from "@/components/SaveButton";
import ShiftReportDepartmentSelect from "@/components/ShiftReportDepartmentSelect";
import PayrollTypeSelect from "@/components/PayrollTypeSelect";
import { PROJECT_DEPENDENCY_DEPARTMENT } from "@/schemas/commonFields";
import {
  ReadPayrollDepartment,
  WritePayrollDepartment,
} from "@/types/payrollDepartment";

interface PayrollDepartmentFormProps {
  footer?: React.ReactNode;
}

export default function PayrollDepartmentForm({
  footer = <SaveButton />,
}: PayrollDepartmentFormProps) {
  const { t } = useTranslation();
  const config = usePayrollDepartmentsDictionary();
  const { Icon, ns } = config;
  const labels = config.getLabels(t);
  const { mutation, query } = useDictionaryContext<
    ReadPayrollDepartment,
    WritePayrollDepartment
  >();

  return (
    <DictionaryForm>
      <PageContent
        icon={<Icon className="w-6 h-6 mr-3" />}
        title={labels.title}
        footer={footer}
      >
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            name="name"
            label={t(`${ns}.fields.name`)}
            required={isRequired(config.schema.shape.name)}
            render={({ field }) => (
              <Input
                disabled={mutation.isPending}
                className="w-full"
                {...field}
              />
            )}
          />{" "}
          <Field
            name="description"
            label={t(`${ns}.fields.description`)}
            render={({ field }) => <Input {...field} className="w-full" />}
          />
          <Field
            name="shift_report_department_id"
            label={t(`${ns}.fields.shift_report_department`)}
            required={isRequired(
              config.schema.shape.shift_report_department_id,
            )}
            render={({ field }) => (
              <ShiftReportDepartmentSelect
                {...field}
                className="w-full"
                loading="eager"
                defaultItem={{
                  id: query?.data?.shift_report_department_id,
                  name: query?.data?.shift_report_department,
                }}
              />
            )}
          />
          <Field
            name="payroll_type_id"
            label={t(`${ns}.fields.payroll_type`)}
            required={isRequired(config.schema.shape.payroll_type_id)}
            render={({ field }) => (
              <PayrollTypeSelect
                {...field}
                className="w-full"
                loading="eager"
                projectDependsOn={PROJECT_DEPENDENCY_DEPARTMENT}
                defaultItem={{
                  id: query?.data?.payroll_type_id,
                  name: query?.data?.payroll_type,
                }}
              />
            )}
          />
        </Fieldset>
      </PageContent>
    </DictionaryForm>
  );
}
